import React, { createContext, useContext, useEffect, useMemo, useState } from "react"
import { callFunction } from "../services/firebase.service"
import {
	forgetSchoolLocation,
	getSchool,
	subscribeToSchool,
	subscribeToSchoolCoaches,
} from "../utils/firestore.utils"
import {
	ARCHIVED_SCHOOL,
	activeSchoolIDs,
	canEditSchool,
	canInviteTo,
	getSchoolSettings,
	initialSchoolID,
	isArchivedSchool,
	isPrimarySchool,
	switchableSchoolIDs,
} from "../utils/school.utils"
import { defaultDisplayedCoachID, seesLessonsOf, teachesInSchool } from "../utils/func.utils"
import { profileContext } from "./profile.provider"

export const schoolContext = createContext()

const SchoolProvider = ({ children }) => {
	const { coach, registerSelectedSchool, setDisplayedCoachID, displayedCoachID } =
		useContext(profileContext)

	const [loadedSchools, setSchools] = useState({}) // { [schoolID]: schoolObj }
	// i maestri dei circoli, quando sono arrivati: { [schoolID]: { [coachID]: coach } }
	const [coachesBySchool, setCoachesBySchool] = useState({})
	// quello che del circolo cambia mentre l'app è aperta: { [schoolID]: {...} }
	const [liveSchools, setLiveSchools] = useState({})

	// Il circolo arriva con i maestri come riferimenti; appena si sa chi sono
	// diventano un dizionario per id, che si tiene aggiornato da solo.
	const schools = useMemo(
		() =>
			Object.fromEntries(
				Object.entries(loadedSchools).map(([schoolID, school]) => {
					const live = { ...school, ...liveSchools[schoolID] }
					return [
						schoolID,
						coachesBySchool[schoolID]
							? { ...live, coaches: coachesBySchool[schoolID] }
							: live,
					]
				}),
			),
		[loadedSchools, coachesBySchool, liveSchools],
	)
	const [selectedSchool, setSelectedSchool] = useState(null)
	const [loading, setLoading] = useState(true)

	// ------------------- HELPERS -------------------
	const canCreateLessonIn = schoolID => teachesInSchool(coach, schoolID)
	const canViewStatsOf = schoolID =>
		teachesInSchool(coach, schoolID) || coach?.managedSchools?.includes(schoolID)
	const getSelectedSchool = () => schools[selectedSchool] || null

	// L'anagrafica del luogo la cambia chi lo possiede o chi dirige il
	// circolo. Le proprie condizioni di lavoro, invece, le cambia chiunque
	// ci insegni: quelle non sono del circolo.
	const canEditSelectedSchool = schoolID => canEditSchool(coach, schools[schoolID], schoolID)
	const canInviteToSchool = schoolID => canInviteTo(coach, schools[schoolID], schoolID)

	const getSelectedSchoolSettings = () =>
		getSchoolSettings(coach, schools[selectedSchool], selectedSchool)

	// I circoli in uso, il principale per primo, più quello archiviato che
	// si sta guardando: sono quelli del selettore. Gli archiviati si
	// ritrovano dal profilo, in "I tuoi circoli".
	const knownSchoolIDs = Object.keys(schools)
	const schoolIDs = switchableSchoolIDs(coach, knownSchoolIDs, selectedSchool)
	const primarySchoolID = knownSchoolIDs.find(id => isPrimarySchool(coach, id)) ?? null

	// Principale, archiviato o di nuovo in uso (`null`). La scelta arriva
	// all'app dal documento del maestro, che si ascolta già. Archiviando il
	// circolo che si ha davanti si passa al primo di quelli in uso: è
	// sparito dall'elenco, e restarci vorrebbe dire non vederlo più andar via.
	const setSchoolPlacement = async (schoolID, placement) => {
		const setPlacement = callFunction("setSchoolPlacement")
		await setPlacement({ schoolID, placement })

		if (placement === ARCHIVED_SCHOOL && schoolID === selectedSchool) {
			const next = activeSchoolIDs(coach, knownSchoolIDs).find(id => id !== schoolID)
			if (next) setSelectedSchool(next)
		}
	}

	// I coach della scuola in memoria sono già stati normalizzati da reference
	// a dizionario: rileggendo il documento tornerebbero reference, e il
	// fetch qui sotto ripartirebbe da capo.
	const refreshSchool = async schoolID => {
		const school = await getSchool(schoolID)
		if (!school) return

		setSchools(prev => ({
			...prev,
			[schoolID]: { ...school, coaches: prev[schoolID]?.coaches ?? school.coaches ?? [] },
		}))
	}

	const updateSchool = async (schoolID, patch) => {
		const update = callFunction("updateSchool")
		await update({ schoolID, patch })

		// l'indirizzo finisce sugli eventi del calendario, ed era in cache
		forgetSchoolLocation(schoolID)
		await refreshSchool(schoolID)
	}

	// Le condizioni del maestro stanno sul suo documento, che l'app ascolta
	// già: non serve rileggere niente, il listener aggiorna da solo.
	const updateSchoolSettings = async (schoolID, patch) => {
		const update = callFunction("updateSchoolSettings")
		await update({ schoolID, patch })
	}

	// I circoli di un maestro cambiano mentre l'app è aperta: ne entra in uno
	// con un codice, ne esce, ne crea o ne chiude. Riascoltare solo il
	// cambio di maestro non basta — il maestro è sempre lo stesso — e la
	// lista resterebbe quella di quando è entrato.
	const schoolsKey = [
		...(coach?.teachesIn ?? []),
		...(coach?.managedSchools ?? []),
		...(coach?.notTeachingIn ?? []),
	]
		.sort()
		.join(",")

	// ------------------- FETCH SCUOLE -------------------
	useEffect(() => {
		const fetchSchools = async () => {
			// Senza maestro non abbiamo finito di guardare: non abbiamo
			// ancora cominciato. Dirsi "pronti" qui fa concludere alle
			// schermate che il circolo non esista.
			if (!coach) {
				setSchools({})
				setSelectedSchool(null)
				return
			}

			setLoading(true)
			try {
				const ids = Array.from(
					new Set([
						...(coach.teachesIn || []),
						...(coach.managedSchools || []),
						...(coach.notTeachingIn || []),
					])
				)

				// fetch scuole senza coach
				const schoolsData = await Promise.all(ids.map(id => getSchool(id)))
				const filtered = schoolsData.filter(Boolean)

				const schoolsDict = {}
				filtered.forEach(school => {
					schoolsDict[school.schoolID] = {
						...school,
						coaches: Array.isArray(school.coaches) ? school.coaches : [], // array di ref per ora
					}
				})

				setSchools(schoolsDict)

				// selezione iniziale: il principale, se no il primo in uso
				const initialSelected =
					selectedSchool && schoolsDict[selectedSchool]
						? selectedSchool
						: initialSchoolID(coach, Object.keys(schoolsDict))

				setSelectedSchool(initialSelected)
			} catch (err) {
				console.error("Errore caricando le scuole:", err)
				setSchools({})
				setSelectedSchool(null)
			} finally {
				setLoading(false)
			}
		}

		fetchSchools()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [coach?.coachID, schoolsKey])

	// ------------------- I MAESTRI DEL CIRCOLO SELEZIONATO -------------------
	// Si ascoltano, non si leggono una volta: un collega entra, esce, smette di
	// insegnare mentre l'app è aperta, e lo stesso vale per sé. Letti una
	// volta all'apertura restavano quelli di allora — senza chi era entrato
	// dopo, e con sé che risultava maestro anche dopo aver smesso — e
	// selettori e conteggi sbagliavano finché non si ricaricava.
	useEffect(() => {
		if (!selectedSchool) return

		return subscribeToSchoolCoaches(selectedSchool, list => {
			// un errore arriva come elenco vuoto: restano i riferimenti di prima
			if (list.length === 0) return
			setCoachesBySchool(prev => ({
				...prev,
				[selectedSchool]: Object.fromEntries(list.map(c => [c.coachID, c])),
			}))
		})
	}, [selectedSchool])

	// ------------------- CHI NE È IL PROPRIETARIO -------------------
	// Di ogni circolo si ascolta chi ne è il proprietario, se lo dirige, e la
	// proposta di passarlo: la riceve anche chi sta guardando un altro
	// circolo, e il circolo passa di mano mentre tutti e due hanno l'app
	// aperta. Il resto del documento — nome, campi, listino — resta quello
	// letto all'apertura e rifatto da `refreshSchool`.
	useEffect(() => {
		const ids = schoolsKey ? [...new Set(schoolsKey.split(","))] : []
		const stops = ids.map(schoolID =>
			subscribeToSchool(schoolID, school =>
				setLiveSchools(prev => ({
					...prev,
					[schoolID]: {
						ownerCoachID: school.ownerCoachID,
						runByOwner: school.runByOwner,
						ownershipOffer: school.ownershipOffer ?? null,
					},
				})),
			),
		)
		return () => stops.forEach(stop => stop())
	}, [schoolsKey])

	// ------------------- REGISTER SELECTED SCHOOL -------------------
	useEffect(() => {
		if (registerSelectedSchool) {
			registerSelectedSchool(selectedSchool)
		}

		// Il maestro mostrato è sé stesso, finché non se ne sceglie un altro
		// dal selettore: partire dal primo del circolo voleva dire caricare le
		// lezioni di qualcuno che magari non si può guardare. Se il permesso
		// se ne va, o il collega esce dal circolo, si torna a sé.
		//
		// Chi qui non insegna non ha una giornata sua, sempre vuota: parte dal
		// primo dei maestri che guarda, e ci torna se quello smette di
		// insegnare. Finché i colleghi sono reference non si sa chi siano, e
		// si aspetta.
		const school = getSelectedSchool()
		if (school && !Array.isArray(school.coaches)) {
			const coachIDs = Object.keys(school.coaches ?? {})
			const displayed = school.coaches?.[displayedCoachID]
			const isMe = displayedCoachID === coach?.coachID
			const stillFits =
				coachIDs.includes(displayedCoachID) &&
				seesLessonsOf(coach, selectedSchool, displayedCoachID) &&
				(isMe
					? teachesInSchool(coach, selectedSchool) ||
						defaultDisplayedCoachID(coach, school, selectedSchool) === coach?.coachID
					: teachesInSchool(displayed, selectedSchool))
			if (!stillFits) {
				const next = defaultDisplayedCoachID(coach, school, selectedSchool)
				if (next !== displayedCoachID) setDisplayedCoachID(next)
			}
		} else if (school && displayedCoachID !== coach?.coachID) {
			setDisplayedCoachID(coach?.coachID)
		}
	}, [selectedSchool, registerSelectedSchool, getSelectedSchool()?.coaches, coach])

	return (
		<schoolContext.Provider
			value={{
				schools,
				schoolIDs,
				primarySchoolID,
				isArchivedSchool: schoolID => isArchivedSchool(coach, schoolID),
				setSchoolPlacement,
				selectedSchool,
				loading,
				getSelectedSchool,
				setSelectedSchool,
				canCreateLessonIn,
				canViewStatsOf,
				canEditSchool: canEditSelectedSchool,
				canInviteTo: canInviteToSchool,
				getSchoolSettings: getSelectedSchoolSettings,
				updateSchool,
				updateSchoolSettings,
			}}>
			{children}
		</schoolContext.Provider>
	)
}

export default SchoolProvider
